import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Secure Reset Page - Token-Safe Password Reset Flow
 * 
 * This page prevents corporate email scanners from consuming Supabase tokens.
 * 
 * Flow:
 * 1. User receives email with link: /secure-reset?token=TOKENHASH&redirect_to=REDIRECT
 * 2. Email scanner opens link (token NOT consumed yet - it's just a hash)
 * 3. User clicks button on this page
 * 4. We redirect to Supabase verify endpoint with the token
 * 5. Supabase verifies and redirects to /reset-password with access_token in hash
 * 6. User can then reset password
 */
export default function SecureReset() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [redirectTo, setRedirectTo] = useState('');

  useEffect(() => {
    // Extract token and redirect_to from URL query parameters
    const tokenParam = searchParams?.get('token');
    const redirectParam = searchParams?.get('redirect_to');

    if (!tokenParam) {
      setError('Invalid reset link: Missing token parameter. Please request a new password reset.');
      return;
    }

    if (!redirectParam) {
      setError('Invalid reset link: Missing redirect parameter. Please request a new password reset.');
      return;
    }

    setToken(tokenParam);
    setRedirectTo(redirectParam);
  }, [searchParams]);

  /**
   * Handle button click - redirect to Supabase verify endpoint
   * This is the ONLY place where the token is actually used
   * Corporate email scanners won't trigger this - only user clicks will
   */
  const handleContinue = () => {
    if (!token || !redirectTo) {
      setError('Missing required parameters. Please request a new password reset.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      // Construct Supabase verify URL
      // Format: https://PROJECT.supabase.co/auth/v1/verify?token=TOKENHASH&type=recovery&redirect_to=REDIRECT
      const verifyUrl = `${supabaseUrl}/auth/v1/verify?token=${encodeURIComponent(token)}&type=recovery&redirect_to=${encodeURIComponent(redirectTo)}`;

      console.log('[SecureReset] Redirecting to Supabase verify endpoint...');
      console.log('[SecureReset] Token hash:', token?.substring(0, 20) + '...');
      console.log('[SecureReset] Redirect to:', redirectTo);

      // Redirect to Supabase verify endpoint
      // Supabase will verify the token and redirect to redirectTo with access_token in hash
      window.location.href = verifyUrl;
    } catch (err) {
      console.error('[SecureReset] Error:', err);
      setError(`Failed to process reset link: ${err?.message || 'Please try again'}`);
      setLoading(false);
    }
  };

  if (error && !token) {
    // Show error if token is missing
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">⚠️ Invalid Reset Link</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={() => {
                // Navigate to login page without preserving any state
                window.location.href = '/';
              }}
              className="w-full flex justify-center py-2 px-4 border border-blue-600 text-sm font-medium rounded-md text-blue-600 hover:bg-blue-50"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset Your Password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Click the button below to continue with your password reset
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Error Messages */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">⚠️ Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
            <p className="text-sm font-medium mb-2">🔒 Secure Password Reset</p>
            <p className="text-xs">
              This secure page protects your reset link from being consumed by email scanners.
              Click the button below to continue with your password reset.
            </p>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={loading || !token || !redirectTo}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Processing...
              </>
            ) : (
              'Continue to Password Reset'
            )}
          </button>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                // Navigate to login page without preserving any state
                window.location.href = '/';
              }}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

